/**
 * 変化の知らせ（試用・2026-10-07）— `/changes`。kalori が毎晩の取得で見つけた御社のメニューの変化のうち、公式値に関わるもの。
 * 材料 = 毎晩の取得の差分 × 御社の台帳（サーバ = src/domain/changes.ts）。週 1 回（月曜の朝）にメールでも件数を知らせる。
 */
import { useEffect, useState, type ReactNode } from "react";
import { api, type ChangeItem, type Changes as ChangesData } from "../api";
import { Link } from "../router";
import { REVIEW_STATE_LABEL, fmtDateTime } from "../format";

const DAYS = [7, 30, 90];

export function Changes() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ChangesData | null>(null);
  const [error, setError] = useState<{ text: string; unlinked: boolean } | null>(null);

  useEffect(() => {
    setData(null);
    void api.changes(days).then((r) => (r.ok ? setData(r.data) : setError({ text: r.error, unlinked: r.code === "merchant_unlinked" })));
  }, [days]);

  const toggleWeekly = async (weekly: boolean) => {
    const r = await api.setWeeklyNotify(weekly);
    if (r.ok && data) setData({ ...data, notifyWeekly: r.data.notifyWeekly });
  };

  return (
    <section className="stack-lg">
      <div className="row-between">
        <div>
          <h1>
            変化の知らせ <span className="badge trial">試用</span>
          </h1>
          <p className="muted">kalori が毎晩、御社サイトのメニューを確かめて見つけた変化のうち、公式値に関わるものです。</p>
        </div>
        <div className="segmented" role="group" aria-label="期間">
          {DAYS.map((d) => (
            <button key={d} type="button" className={d === days ? "on" : ""} onClick={() => setDays(d)}>
              {d} 日
            </button>
          ))}
        </div>
      </div>
      {error &&
        (error.unlinked ? (
          <p className="notice">
            まだブランドが紐付いていません。<Link href="/">ホーム</Link>から紐付けてください。
          </p>
        ) : (
          <p className="error">{error.text}</p>
        ))}
      {!error && !data && <p className="muted">読み込んでいます…</p>}
      {data && (
        <>
          <Group
            title="新しく載ったメニュー（公式値がまだ無い）"
            note="公式値を入力すると、kalori の推定から置き換わります。"
            items={data.newWithoutOfficial}
            action={(i) => (i.productDbId ? <Link href={`/catalog/${i.productDbId}`}>公式値を入力する</Link> : null)}
          />
          <Group
            title="公式値があるメニューが、御社サイトで見当たらなくなった"
            note="終売・改名の可能性があります。改名なら、新しいメニューで入力し直してください。"
            items={data.discontinued}
          />
          <Group
            title="公式値があるメニューで、御社サイトの栄養成分の表記が変わった"
            note="値を変えた場合は、公開中の公式値も改訂してください（改訂は承認まで今の値のまま）。"
            items={data.nutritionChanged}
          />
          <Group
            title="提供期間を過ぎても公開中の公式値"
            note="公開を止める場合は、画面上部の「ご意見・ご要望」から運営にお知らせください。"
            items={data.pastAvailability}
          />
          <div className="card">
            <label className="check-item">
              <input type="checkbox" checked={data.notifyWeekly} onChange={(e) => void toggleWeekly(e.target.checked)} />
              毎週月曜の朝に、先週の変化の件数をメールで受け取る（変化が無い週は送りません）
            </label>
          </div>
        </>
      )}
    </section>
  );
}

function Group({ title, note, items, action }: { title: string; note: string; items: ChangeItem[]; action?: (i: ChangeItem) => ReactNode }) {
  return (
    <div className="card flush">
      <h2 className="card-title">
        {title} <span className="muted">{items.length}</span>
      </h2>
      <p className="muted small card-title">{note}</p>
      {items.length === 0 ? (
        <p className="muted small card-title">ありません。</p>
      ) : (
        <ul className="list">
          {items.map((i, k) => (
            <li key={`${i.productDbId}-${k}`} className="list-row">
              <span>
                {i.entry ? <Link href={`/entries/${i.entry.id}`}>{i.name}</Link> : i.name}{" "}
                <span className="muted small">
                  {fmtDateTime(i.at.length === 10 ? `${i.at} 00:00:00` : i.at)}
                  {i.entry && ` ・ ${REVIEW_STATE_LABEL[i.entry.reviewState as keyof typeof REVIEW_STATE_LABEL] ?? i.entry.reviewState}`}
                </span>
              </span>
              {action?.(i)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
